import { unwrap } from "@sagot/api-client";
import { AUDIT_PAGE_SIZE } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, DatePicker, EmptyState, Icon, Input, Select, TableSkeletonRows } from "@sagot/ui";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { useDebounced } from "../../../../lib/slug";
import { useMembers } from "../../../../roles/use-roles";
import { usePermissions } from "../../../../session/permissions";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceId } from "../../../../session/workspace-context";
import { useFormatters, wsKeys, type AuditLogOut } from "../../shared";
import { humanizeAction } from "../settings-model";
import { TRAILS, TRAIL_PARAM, actionKey, parseTrail, trailPrefix, useAuditCsv, type Trail } from "./audit-trails";
import { PanelError, TableScroll } from "./panel-kit";

const COLUMNS = 5;
export const ACTOR_PARAM = "actor";

interface Filters {
  q: string;
  since: string;
  until: string;
  actor: string | null;
  prefix: string | null;
}

/** `YYYY-MM-DD` → ISO bounds of that day (since: start, until: end). */
export function dayBound(date: string, end: boolean): string | null {
  if (!date) return null;
  return new Date(`${date}T${end ? "23:59:59.999" : "00:00:00.000"}`).toISOString();
}

function useAuditLog(filters: Filters) {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useInfiniteQuery({
    queryKey: wsKeys.audit(wsId, JSON.stringify(filters)),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      unwrap(
        api.GET("/v1/audit-logs", {
          params: { query: { q: filters.q || null, since: dayBound(filters.since, false), until: dayBound(filters.until, true), actor_id: filters.actor, action_prefix: filters.prefix, cursor: pageParam, limit: AUDIT_PAGE_SIZE } },
        }),
      ),
    getNextPageParam: (page) => page.next_cursor ?? null,
  });
}

function AuditRow({ row }: { row: AuditLogOut }) {
  const { t } = useTranslation();
  const format = useFormatters();
  return (
    <tr>
      <td className="whitespace-nowrap text-fg-muted">{format.dateTime(row.occurred_at)}</td>
      <td className="font-semibold">{row.actor_name ?? t("settings.audit.system")}</td>
      <td>{t(`privacyM29.audit.events.${actionKey(row.action)}`, { defaultValue: t(`settings.audit.actions.${actionKey(row.action)}`, { defaultValue: humanizeAction(row.action) }) })}</td>
      <td>
        {row.target_label ?? row.target_type ?? "—"}
        {row.impersonated_by ? <span className="ml-1 text-xs text-fg-subtle">{t("settings.audit.viaSupport")}</span> : null}
      </td>
      <td className="font-mono text-xs text-fg-muted">{row.ip ?? "—"}</td>
    </tr>
  );
}

/** Audit log panel: search, date range, member filter (`?actor=`), trail filter (`?trail=launch|customer|api_key`), cursor paging, CSV from the server with the same filters. */
export function AuditPanel() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const actor = params.get(ACTOR_PARAM);
  const [q, setQ] = useState("");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const trail = parseTrail(params.get(TRAIL_PARAM));
  const filters: Filters = { q: useDebounced(q.trim()), since, until, actor, prefix: trailPrefix(trail) };
  const log = useAuditLog(filters);
  const permissions = usePermissions();
  const canSeeMembers = permissions.can("team.view");
  const canExport = permissions.can("data.export");
  const csv = useAuditCsv();
  const members = useMembers();
  const rows = log.data?.pages.flatMap((page) => page.items) ?? [];
  const actorName = actor ? (canSeeMembers ? members.data?.find((m) => m.user_id === actor)?.name : undefined) ?? rows.find((r) => r.actor_id === actor)?.actor_name ?? t("settings.audit.oneMember") : null;
  const exportCsv = () => {
    csv.mutate({ q: filters.q || null, since: dayBound(since, false), until: dayBound(until, true), actor_id: actor, action_prefix: filters.prefix });
  };
  const setTrail = (next: Trail) => {
    if (next === "all") params.delete(TRAIL_PARAM);
    else params.set(TRAIL_PARAM, next);
    setParams(params, { replace: true });
  };
  if (log.isError) return <PanelError error={log.error} onRetry={() => void log.refetch()} />;
  return (
    <section className="card overflow-hidden" aria-labelledby="audit-title">
      <div className="card-header flex-wrap gap-3">
        <div>
          <h2 id="audit-title" className="card-title">
            {t("settings.audit.title")}
          </h2>
          <p className="mt-0.5 text-xs text-fg-subtle">{t("privacyM29.audit.subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select className="h-8 w-40 text-xs" aria-label={t("privacyM29.audit.trailLabel")} value={trail} onChange={(event) => { setTrail(parseTrail(event.target.value)); }} data-testid="audit-trail">
            {TRAILS.map((option) => (
              <option key={option} value={option}>
                {t(`privacyM29.audit.trails.${option}`)}
              </option>
            ))}
          </Select>
          <Input className="h-8 w-60 text-xs" placeholder={t("settings.audit.filter")} aria-label={t("settings.audit.filter")} value={q} onChange={(event) => { setQ(event.target.value); }} />
          <DatePicker className="h-8 w-36 text-xs" aria-label={t("settings.audit.since")} value={since} max={until || undefined} onValueChange={setSince} />
          <DatePicker className="h-8 w-36 text-xs" aria-label={t("settings.audit.until")} value={until} min={since || undefined} onValueChange={setUntil} />
          <Button variant="secondary" size="sm" disabled={rows.length === 0 || !canExport} loading={csv.isPending} title={canExport ? undefined : t("console.noPermission")} onClick={exportCsv} data-testid="audit-export">
            <Icon name="download" />
            {t("settings.audit.export")}
          </Button>
        </div>
      </div>
      {actorName ? (
        <div className="flex items-center gap-2 border-b border-line px-5 py-2.5 text-xs">
          <span className="inline-flex items-center gap-1.5 rounded-pill bg-primary-soft px-2.5 py-1 font-semibold text-primary-ink">
            {t("settings.audit.filteredTo", { name: actorName })}
            <button
              type="button"
              className="inline-flex size-6 items-center justify-center rounded-pill hover:bg-surface"
              aria-label={t("settings.audit.clearActor")}
              onClick={() => {
                params.delete(ACTOR_PARAM);
                setParams(params, { replace: true });
              }}
            >
              <Icon name="x" className="size-3.5" />
            </button>
          </span>
        </div>
      ) : null}
      {!log.isPending && rows.length === 0 ? (
        <EmptyState icon="scroll-text" headingLevel={3} title={t("settings.audit.emptyTitle")} description={t("settings.audit.emptyBody")} />
      ) : (
        <TableScroll label={t("settings.audit.title")}>
          <table className="table" data-testid="audit-log">
            <thead>
              <tr>
                <th>{t("settings.audit.colTime")}</th>
                <th>{t("settings.audit.colActor")}</th>
                <th>{t("settings.audit.colAction")}</th>
                <th>{t("settings.audit.colTarget")}</th>
                <th>{t("settings.audit.colIp")}</th>
              </tr>
            </thead>
            <tbody>{log.isPending ? <TableSkeletonRows rows={6} columns={COLUMNS} /> : rows.map((row) => <AuditRow key={row.id} row={row} />)}</tbody>
          </table>
        </TableScroll>
      )}
      {log.hasNextPage ? (
        <div className="flex justify-center border-t border-line px-5 py-3">
          <Button variant="secondary" size="sm" loading={log.isFetchingNextPage} onClick={() => void log.fetchNextPage()}>
            {t("settings.audit.loadMore")}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
